import { SmallText } from '@/components/common/Typo'
import { promotionAppliedOnTypes, promotionTypes } from '@/utils/promotion'
import { TFnType, useTranslate } from '@tolgee/react'
import { Card, cn } from 'oddle-shop-monorepo.design-system'
import PromosBadges from '@/components/modals/CheckoutPromotionModal/PromosBadges'
import useAppContext from '@/hooks/useAppContext'

export const getPromotionAvailabilityText = ({
  t,
  promotion,
}: {
  t: TFnType
  promotion: {
    appliedOn: number
  }
}): string => {
  switch (promotion.appliedOn) {
    case promotionAppliedOnTypes.pickup:
      return t('Valid for Pickup only!')
    case promotionAppliedOnTypes.delivery:
      return t('Valid for Delivery only!')
    case promotionAppliedOnTypes.both:
      return t('Valid for Pickup and Delivery!')
    default:
      return ''
  }
}

export const amountTypes = {
  percentage: 0,
  amount: 1,
}

const promoApplyTypes = {
  firstTime: 0,
  repeat: 1,
  both: 2,
}

export const getPromoApplyType = ({
  t,
  promotion,
}: {
  t: TFnType
  promotion: Promotion
}) => {
  if (promotion.promoApplyTo === promoApplyTypes.firstTime) {
    return t('For first time customers only')
  }
  if (promotion.promoApplyTo === promoApplyTypes.repeat) {
    return t('For repeat customers only')
  }
  return t('For first time and repeat customers')
}

const getChinesePercentageDiscountAmount = (amount: number) => {
  const reverse = 100 - amount
  return reverse % 10 === 0 ? reverse / 10 : Math.floor(reverse)
}

export const generatePromoDescription = ({
  t,
  promotion,
  formatMoney,
}: {
  t: TFnType
  promotion: Promotion
  formatMoney: (number: number) => string
}): string | undefined => {
  const percentageDiscountAmount = promotion.resultAmount
  const chinesePercentageDiscountAmount = getChinesePercentageDiscountAmount(
    promotion.resultAmount,
  )
  const dollarDiscountAmount = formatMoney(promotion.resultAmount)
  const resultAmountType = promotion.resultAmountType
  const formattedDiscountAmount = {
    percentageDiscountAmount,
    chinesePercentageDiscountAmount,
    dollarDiscountAmount,
  }
  const isFree = promotion.resultAmount === 0

  switch (promotion.promotionResultType) {
    case promotionTypes.applySelectedItem:
      return resultAmountType === amountTypes.percentage
        ? t(
            '{percentageDiscountAmount} Off Selected Item(s)!',
            formattedDiscountAmount,
          )
        : t(
            '{dollarDiscountAmount} Off Selected Item(s)!',
            formattedDiscountAmount,
          )
    case promotionTypes.minimumOrder:
      return t('{dollarDiscountAmount} Minimum Order!', formattedDiscountAmount)
    case promotionTypes.totalOrder:
      return resultAmountType === amountTypes.percentage
        ? t(
            '{percentageDiscountAmount} Off Total Order!',
            formattedDiscountAmount,
          )
        : t('{dollarDiscountAmount} Off Total Order!', formattedDiscountAmount)
    case promotionTypes.freeItem:
      return t('Free Item!', formattedDiscountAmount)
    case promotionTypes.deliveryFree:
      return isFree
        ? t('Free Delivery!')
        : resultAmountType === amountTypes.percentage
          ? t(
              '{percentageDiscountAmount} Off Delivery Charges!',
              formattedDiscountAmount,
            )
          : t(
              '{dollarDiscountAmount} Off Delivery Charges!',
              formattedDiscountAmount,
            )
    case promotionTypes.discountNextItem:
      return resultAmountType === amountTypes.percentage
        ? t(
            '{percentageDiscountAmount} Off Next Item!',
            formattedDiscountAmount,
          )
        : t('{dollarDiscountAmount} Off Next Item!', formattedDiscountAmount)
  }
}

const PromotionCard = ({ promotion }: { promotion: Promotion }) => {
  const { t } = useTranslate()
  const { formatMoney } = useAppContext()
  return (
    <Card
      className={cn(
        'bg-container flex h-full w-full flex-row justify-between overflow-hidden whitespace-normal rounded-2xl border border-solid border-[#D9D9D952] px-2.5 py-2 shadow-none hover:cursor-pointer md:px-3 md:py-2.5',
      )}
    >
      <div className={cn('flex h-full w-full flex-col gap-1 text-left')}>
        <div className="flex flex-row items-center">
          <div className="mr-2 flex">
            <PromosBadges promotion={promotion} className="md:h-6 md:w-6" />
          </div>
          <SmallText
            textAlign="left"
            className={cn(
              'text-headings m-0 line-clamp-1 !text-sm font-bold md:!text-sm',
              promotion.promoCode ? '' : 'md:line-clamp-2',
            )}
          >
            {promotion.name}
          </SmallText>
        </div>
        <SmallText className={cn('m-0 line-clamp-2 !text-xs')}>
          {promotion.description ||
            generatePromoDescription({
              t,
              promotion,
              formatMoney,
            })}
        </SmallText>
        {promotion.promoCode ? (
          <SmallText className="text-headings m-0 font-bold uppercase leading-5">
            {promotion.promoCode}
          </SmallText>
        ) : null}
      </div>
    </Card>
  )
}

export default PromotionCard
